import { ArrowLeft, IdCard, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsRh, useAgents, useFicheRh, useMentionsRh, useSituationRh } from '@/api/hooks';
import { LIBELLE_REGIME, type FicheRh, type FicheRhSaisie, type RegimeConge, type SituationRhSaisie } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, montant } from '@/lib/utils';

const erreur = (e: unknown, defaut: string) => toast.error(e instanceof Error ? e.message : defaut);

/** Fiche RH d'un agent (lot RH-1) : champs RH, situation confidentielle versionnée et mentions disciplinaires, selon les droits calculés par l'API. */
export function FicheRhPage() {
  const { id } = useParams();
  const moi = useSession()!.utilisateur;
  const { data: f, isLoading, error } = useFicheRh(id);
  const situation = useSituationRh(id, Boolean(f?.droits.voirSituation));
  const mentions = useMentionsRh(id, Boolean(f?.droits.voirMentions));
  const [fiche, setFiche] = useState(false);
  const [sit, setSit] = useState(false);
  const [mention, setMention] = useState(false);
  if (isLoading) return <Chargement />;
  if (error || !f) return <ErreurBloc erreur={error} />;
  const gestion = moi.profils.some((p) => p === 'DIRECTEUR_RH' || p === 'RESSOURCES_HUMAINES' || p === 'ADMINISTRATEUR' || p === 'DIRECTEUR_GENERAL');
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to={gestion ? '/rh/agents' : '/rh'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {gestion ? 'Gestion RH' : 'Mes démarches RH'}
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {f.prenom} {f.nom} {!f.actif && <Badge variant="destructive">désactivé</Badge>} {!f.eligibleRh && <Badge variant="outline">non éligible aux démarches RH</Badge>}
          </span>
        }
        sousTitre={`${f.fonction} · ${f.structure?.libelle ?? '—'} · ${f.matricule}`}
        actions={
          <span className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to={moi.agentId === f.id ? '/rh/mon-dossier' : `/rh/agents/${f.id}/dossier`}>
                <IdCard className="size-4" /> Dossier HRMS
              </Link>
            </Button>
            {f.droits.modifierFiche && (
              <Button variant="outline" onClick={() => setFiche(true)}>
                <Pencil className="size-4" /> Modifier la fiche RH
              </Button>
            )}
          </span>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card data-testid="fiche-rh">
            <CardHeader>
              <CardTitle>Fiche RH</CardTitle>
              <CardDescription>Grade, supérieur hiérarchique, dates et régime : tenus par les Ressources humaines, changements journalisés.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div><span className="text-muted-foreground">Grade</span><div>{f.grade ?? '—'}</div></div>
              <div><span className="text-muted-foreground">Supérieur hiérarchique</span><div>{f.superieur ? `${f.superieur.nom} · ${f.superieur.fonction}` : '—'}</div></div>
              <div><span className="text-muted-foreground">Date d'engagement</span><div>{f.dateEngagement ? dateFr(f.dateEngagement) : '—'}</div></div>
              <div><span className="text-muted-foreground">Ancienneté reconnue depuis</span><div>{f.dateAnciennete ? dateFr(f.dateAnciennete) : 'la date d’engagement'}</div></div>
              <div><span className="text-muted-foreground">Ancienneté révolue</span><div>{f.anciennete ? `${f.anciennete.annees} an${f.anciennete.annees > 1 ? 's' : ''} et ${f.anciennete.mois} mois` : '—'}{f.douzeMoisDeService ? '' : ' · moins de douze mois de service'}</div></div>
              <div><span className="text-muted-foreground">Période de droits en cours</span><div>{f.periodeEnCours ? `du ${dateFr(f.periodeEnCours.debut)} au ${dateFr(f.periodeEnCours.fin)}` : '—'}</div></div>
              <div className="sm:col-span-2">
                <span className="text-muted-foreground">Régime de congé annuel</span>
                <div>
                  {f.regimeConge ? `${LIBELLE_REGIME[f.regimeConge]} · confirmé le ${dateFr(f.regimeConfirmeLe)}` : <Badge variant="outline">À confirmer · proposé d'après la catégorie : {LIBELLE_REGIME[f.regimePropose].split(' (')[0]}</Badge>}
                  {f.droitAnnuel && (
                    <div className="text-muted-foreground">
                      {f.droitAnnuel.base} jours + {f.droitAnnuel.majoration} d'ancienneté = <span className="font-medium text-foreground">{f.droitAnnuel.total} jours ouvrables</span> pour la période en cours
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {f.droits.voirMentions && (
            <Card data-testid="mentions-rh">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Questions disciplinaires
                  {f.droits.ajouterMention && (
                    <Button size="sm" variant="outline" onClick={() => setMention(true)}>
                      <Plus className="size-4" /> Ajouter
                    </Button>
                  )}
                </CardTitle>
                <CardDescription>Réservées aux Ressources humaines et au Directeur Général ; mentionnées sans détail dans les avis sur les prêts.</CardDescription>
              </CardHeader>
              <CardContent>
                {mentions.isLoading ? (
                  <Chargement />
                ) : !mentions.data?.length ? (
                  <p className="text-sm text-muted-foreground">Aucune mention.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Objet</TableHead>
                        <TableHead>Référence</TableHead>
                        <TableHead>Saisie par</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mentions.data.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>{dateFr(m.date)}</TableCell>
                          <TableCell>{m.objet}</TableCell>
                          <TableCell>{m.reference ?? '—'}</TableCell>
                          <TableCell>{m.saisiPar ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {f.droits.voirSituation && (
            <Card data-testid="situation-rh">
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                  Situation confidentielle
                  {f.droits.modifierSituation && (
                    <Button size="sm" variant="outline" onClick={() => setSit(true)}>
                      <Pencil className="size-4" /> Nouvelle version
                    </Button>
                  )}
                </CardTitle>
                <CardDescription>Salaire, retraite et décompte final : agent, RH, Division des investissements, DF, DGA Administration et Finances et DG ; chaque consultation est journalisée.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {situation.isLoading ? (
                  <Chargement />
                ) : !situation.data ? (
                  <p className="text-muted-foreground">Aucune situation enregistrée.</p>
                ) : (
                  <>
                    <div><span className="text-muted-foreground">Salaire net contractuel</span><div>{situation.data.salaireNet === null ? '—' : `${situation.data.salaireNet.toLocaleString('fr-FR')} ${situation.data.devise}`}</div></div>
                    <div><span className="text-muted-foreground">Départ à la retraite prévu</span><div>{situation.data.dateRetraitePrevue ? dateFr(situation.data.dateRetraitePrevue) : '—'}</div></div>
                    <div><span className="text-muted-foreground">Décompte final estimé</span><div>{situation.data.decompteFinalEstime === null ? '—' : montant(situation.data.decompteFinalEstime)}{situation.data.dateCalculDecompte ? ` · calculé le ${dateFr(situation.data.dateCalculDecompte)}` : ''}</div></div>
                    {situation.data.observations && <div><span className="text-muted-foreground">Observations</span><div>{situation.data.observations}</div></div>}
                    <div className="text-xs text-muted-foreground">Version {situation.data.version} · {situation.data.saisiPar?.nom ?? '—'} · {dateFr(situation.data.date)}</div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {fiche && <DialogueFiche fiche={f} onClose={() => setFiche(false)} />}
      {sit && <DialogueSituation id={f.id} precedente={situation.data ?? null} onClose={() => setSit(false)} />}
      {mention && <DialogueMention id={f.id} onClose={() => setMention(false)} />}
    </div>
  );
}

function DialogueFiche({ fiche, onClose }: { fiche: FicheRh; onClose: () => void }) {
  const actions = useActionsRh();
  const agents = useAgents();
  const [v, setV] = useState<Required<FicheRhSaisie>>({ grade: fiche.grade ?? '', superieurId: fiche.superieur?.id ?? '', dateEngagement: fiche.dateEngagement ?? '', dateAnciennete: fiche.dateAnciennete ?? '', regimeConge: fiche.regimeConge ?? '', eligibleRh: fiche.eligibleRh });
  const enregistrer = async () => {
    try {
      await actions.modifierFiche.mutateAsync({ id: fiche.id, corps: { grade: v.grade, superieurId: v.superieurId, dateEngagement: v.dateEngagement, dateAnciennete: v.dateAnciennete, regimeConge: v.regimeConge, eligibleRh: v.eligibleRh } });
      toast.success('Fiche RH enregistrée.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Modifier la fiche RH de {fiche.prenom} {fiche.nom}</DialogTitle>
          <DialogDescription>Les changements sont journalisés avec l'ancienne et la nouvelle valeur. Le régime proposé d'après la catégorie est « {LIBELLE_REGIME[fiche.regimePropose].split(' (')[0]} ».</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="grade">Grade</Label>
            <Input id="grade" value={v.grade ?? ''} onChange={(e) => setV({ ...v, grade: e.target.value })} placeholder="Chef de service, échelon 1" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="superieur">Supérieur hiérarchique</Label>
            <SelectNatif id="superieur" value={v.superieurId ?? ''} onChange={(e) => setV({ ...v, superieurId: e.target.value })}>
              <option value="">Aucun (membre de la Direction Générale superviseur)</option>
              {(agents.data ?? [])
                .filter((a) => a.id !== fiche.id)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.prenom} {a.nom} · {a.fonction}
                  </option>
                ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="engagement">Date d'engagement</Label>
            <Input id="engagement" type="date" value={v.dateEngagement ?? ''} onChange={(e) => setV({ ...v, dateEngagement: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="anciennete">Date d'ancienneté reconnue (si différente)</Label>
            <Input id="anciennete" type="date" value={v.dateAnciennete ?? ''} onChange={(e) => setV({ ...v, dateAnciennete: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="regime">Régime de congé annuel</Label>
            <SelectNatif id="regime" value={v.regimeConge ?? ''} onChange={(e) => setV({ ...v, regimeConge: e.target.value as RegimeConge | '' })}>
              <option value="">Non confirmé</option>
              <option value="CADRE_DIRECTION">{LIBELLE_REGIME.CADRE_DIRECTION}</option>
              <option value="AGENT">{LIBELLE_REGIME.AGENT}</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="eligible">Éligibilité aux démarches RH</Label>
            <SelectNatif id="eligible" value={v.eligibleRh ? 'oui' : 'non'} onChange={(e) => setV({ ...v, eligibleRh: e.target.value === 'oui' })}>
              <option value="oui">Éligible</option>
              <option value="non">Non éligible (consultant, stagiaire…)</option>
            </SelectNatif>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={enregistrer} disabled={actions.modifierFiche.isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueSituation({ id, precedente, onClose }: { id: string; precedente: { salaireNet: number | null; devise: string; dateRetraitePrevue: string | null; decompteFinalEstime: number | null; dateCalculDecompte: string | null; observations: string | null } | null; onClose: () => void }) {
  const actions = useActionsRh();
  const [v, setV] = useState({ salaireNet: precedente?.salaireNet?.toString() ?? '', devise: (precedente?.devise as 'USD' | 'CDF') ?? 'USD', dateRetraitePrevue: precedente?.dateRetraitePrevue ?? '', decompteFinalEstime: precedente?.decompteFinalEstime?.toString() ?? '', dateCalculDecompte: precedente?.dateCalculDecompte ?? '', observations: precedente?.observations ?? '' });
  const enregistrer = async () => {
    const corps: SituationRhSaisie = {
      salaireNet: v.salaireNet === '' ? null : Number(v.salaireNet),
      devise: v.devise,
      dateRetraitePrevue: v.dateRetraitePrevue || null,
      decompteFinalEstime: v.decompteFinalEstime === '' ? null : Number(v.decompteFinalEstime),
      dateCalculDecompte: v.dateCalculDecompte || null,
      observations: v.observations || null,
    };
    try {
      await actions.enregistrerSituation.mutateAsync({ id, corps });
      toast.success('Nouvelle version de la situation enregistrée.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Situation confidentielle : nouvelle version</DialogTitle>
          <DialogDescription>Le décompte final est calculé manuellement par les RH (spécifications démarches RH, § 5.3) ; la version précédente est conservée.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="salaire">Salaire net contractuel mensuel</Label>
            <Input id="salaire" type="number" min={0} step="0.01" value={v.salaireNet} onChange={(e) => setV({ ...v, salaireNet: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="devise">Devise</Label>
            <SelectNatif id="devise" value={v.devise} onChange={(e) => setV({ ...v, devise: e.target.value as 'USD' | 'CDF' })}>
              <option value="USD">USD</option>
              <option value="CDF">CDF</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="retraite">Départ à la retraite prévu</Label>
            <Input id="retraite" type="date" value={v.dateRetraitePrevue} onChange={(e) => setV({ ...v, dateRetraitePrevue: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="decompte">Décompte final estimé (USD)</Label>
            <Input id="decompte" type="number" min={0} step="0.01" value={v.decompteFinalEstime} onChange={(e) => setV({ ...v, decompteFinalEstime: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="calcul">Date du calcul du décompte</Label>
            <Input id="calcul" type="date" value={v.dateCalculDecompte} onChange={(e) => setV({ ...v, dateCalculDecompte: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="obs">Observations</Label>
            <Textarea id="obs" rows={3} value={v.observations} onChange={(e) => setV({ ...v, observations: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={enregistrer} disabled={actions.enregistrerSituation.isPending}>
            Enregistrer la version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueMention({ id, onClose }: { id: string; onClose: () => void }) {
  const actions = useActionsRh();
  const [v, setV] = useState({ date: '', objet: '', reference: '' });
  const enregistrer = async () => {
    try {
      await actions.ajouterMention.mutateAsync({ id, corps: { date: v.date, objet: v.objet, reference: v.reference || undefined } });
      toast.success('Mention ajoutée.');
      onClose();
    } catch (e) {
      erreur(e, 'Ajout impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter une question disciplinaire</DialogTitle>
          <DialogDescription>Visible des Ressources humaines et du Directeur Général seulement.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="mdate">Date</Label>
            <Input id="mdate" type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mobjet">Objet</Label>
            <Input id="mobjet" value={v.objet} onChange={(e) => setV({ ...v, objet: e.target.value })} placeholder="Avertissement écrit" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mref">Référence</Label>
            <Input id="mref" value={v.reference} onChange={(e) => setV({ ...v, reference: e.target.value })} placeholder="RH/2026/017" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={enregistrer} disabled={actions.ajouterMention.isPending || !v.date || v.objet.trim().length < 3}>
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
